/**
 * Objektübersicht — reine Filter-/Sort-/Ableitungslogik (F1).
 *
 * Objekte sind niedrig-kardinal (Gebäude eines Tenants); die Liste kommt als
 * ein `GET /property-groups`-Payload und wird client-seitig gefiltert/sortiert/
 * paginiert. Dieselbe Filter-State treibt Listen- und Kartenansicht (identische
 * Resultate). `apartments`/`visibleApartmentCount` sind bereits scope-gefiltert
 * (Backend §6.63) — hier entstehen keine versteckten Counts.
 */
import type { PropertyGroupResponse, PropertyLifecycleStatus } from '@/shared/api';

export type ObjectStatusFilter = 'all' | 'review' | PropertyLifecycleStatus;
export type ObjectSort = 'name-asc' | 'name-desc' | 'apartments-desc' | 'review-first';
export type ObjectView = 'list' | 'cards';

export interface LifecycleCounts {
  active: number;
  draft: number;
  paused: number;
  archived: number;
}

export function lifecycleCounts(group: PropertyGroupResponse): LifecycleCounts {
  const counts: LifecycleCounts = { active: 0, draft: 0, paused: 0, archived: 0 };
  for (const apartment of group.apartments) {
    if (apartment.status === 'Active') counts.active += 1;
    else if (apartment.status === 'Draft') counts.draft += 1;
    else if (apartment.status === 'Paused') counts.paused += 1;
    else if (apartment.status === 'Archived') counts.archived += 1;
  }
  return counts;
}

const STATUS_LABEL: Record<PropertyLifecycleStatus, string> = {
  Active: 'aktiv',
  Draft: 'Entwurf',
  Paused: 'pausiert',
  Archived: 'archiviert',
};

/** Kompakte, editoriale Count-Zeile: „3 aktiv · 1 Entwurf" (Nullwerte entfallen). */
export function lifecycleSummary(group: PropertyGroupResponse): string {
  const counts = lifecycleCounts(group);
  const parts: string[] = [];
  if (counts.active) parts.push(`${counts.active} ${STATUS_LABEL.Active}`);
  if (counts.draft) parts.push(`${counts.draft} ${STATUS_LABEL.Draft}`);
  if (counts.paused) parts.push(`${counts.paused} ${STATUS_LABEL.Paused}`);
  if (counts.archived) parts.push(`${counts.archived} ${STATUS_LABEL.Archived}`);
  return parts.join(' · ');
}

function searchText(group: PropertyGroupResponse): string {
  const { name, address } = group;
  return [name, address.street, address.postalCode, address.city]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function matchesStatus(group: PropertyGroupResponse, status: ObjectStatusFilter): boolean {
  if (status === 'all') return true;
  if (status === 'review') return group.needsAddressReview;
  return group.apartments.some((apartment) => apartment.status === status);
}

const collator = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

function sortObjects(groups: PropertyGroupResponse[], sort: ObjectSort): PropertyGroupResponse[] {
  const byName = (a: PropertyGroupResponse, b: PropertyGroupResponse) =>
    collator.compare(a.name, b.name);
  const sorted = [...groups];
  switch (sort) {
    case 'name-desc':
      return sorted.sort((a, b) => byName(b, a));
    case 'apartments-desc':
      return sorted.sort(
        (a, b) => b.visibleApartmentCount - a.visibleApartmentCount || byName(a, b),
      );
    case 'review-first':
      return sorted.sort(
        (a, b) => Number(b.needsAddressReview) - Number(a.needsAddressReview) || byName(a, b),
      );
    case 'name-asc':
    default:
      return sorted.sort(byName);
  }
}

export interface ObjectFilterState {
  search: string;
  status: ObjectStatusFilter;
  sort: ObjectSort;
}

/** Filtert nach Suchtext + Status und sortiert — Grundlage beider Views. */
export function filterAndSortObjects(
  groups: PropertyGroupResponse[],
  { search, status, sort }: ObjectFilterState,
): PropertyGroupResponse[] {
  const needle = search.trim().toLowerCase();
  const filtered = groups.filter(
    (group) =>
      matchesStatus(group, status) && (needle === '' || searchText(group).includes(needle)),
  );
  return sortObjects(filtered, sort);
}
